import { useState } from "react";
import axios from "axios";
import { FaCampground } from "react-icons/fa";

// Leading prompts shown beside the text area. Framed as optional inspiration so
// reviewers touch on these topics without feeling like they must answer each.
const PROMPTS = [
  "Had you built Lightning Web Components (or written JavaScript/HTML) before this course? If not, what was it like building your first ones with CampLWC?",
  "What was your experience like learning through CampLWC specifically?",
  "How did the bite-size challenge format work for you?",
  "Was the way the course was laid out helpful to how you learn?",
  "Who would you recommend this course to, and why?",
];

function ReviewForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "",
    company: "",
    linkedin: "",
    review: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post("/api/testimonials", { ...form, course: "camp-lwc" });
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong submitting your testimonial. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Inputs sit on a darker, well-defined board against the white card.
  const inputClasses =
    "w-full rounded-lg bg-stone-100 border border-stone-300 px-3.5 py-2.5 text-stone-800 placeholder-stone-400 outline-none transition focus:border-orange-500 focus:bg-white focus:ring-2 focus:ring-orange-100";

  return (
    <div className="min-h-screen bg-stone-100 text-stone-800 flex flex-col items-center px-4 py-12">
      {/* Brand */}
      <div className="flex items-center gap-2 mb-6 text-lg font-bold text-stone-700">
        <FaCampground className="text-2xl text-orange-500" />
        <span>Camp Apex</span>
      </div>

      <div className="w-full max-w-5xl bg-white border border-stone-200 rounded-xl shadow-sm p-8 md:p-10">
        {submitted ? (
          <div className="text-center py-16">
            <h1 className="text-2xl font-bold mb-2 text-stone-900">Thank you!</h1>
            <p className="text-stone-500 max-w-md mx-auto">
              Your testimonial has been submitted. We really appreciate you taking the time to share your CampLWC story.
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl md:text-3xl font-bold mb-2 text-stone-900">
              Share your CampLWC experience
            </h1>
            <p className="text-stone-500 mb-8 max-w-2xl">
              Tell us how learning Lightning Web Components with CampLWC went for you. Your words help others decide to take the leap.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium mb-1.5 text-stone-700" htmlFor="name">
                    Name <span className="text-orange-500">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={handleChange}
                    className={inputClasses}
                    placeholder="Jane Developer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5 text-stone-700" htmlFor="email">
                    Email <span className="text-orange-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className={inputClasses}
                    placeholder="jane@example.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium mb-1.5 text-stone-700" htmlFor="role">
                    Current role in the ecosystem <span className="text-orange-500">*</span>
                  </label>
                  <input
                    id="role"
                    name="role"
                    type="text"
                    required
                    value={form.role}
                    onChange={handleChange}
                    className={inputClasses}
                    placeholder="Salesforce Admin"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5 text-stone-700" htmlFor="company">
                    Current company{" "}
                    <span className="text-stone-400 text-xs font-normal">(optional)</span>
                  </label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    value={form.company}
                    onChange={handleChange}
                    className={inputClasses}
                    placeholder="Acme Corp"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5 text-stone-700" htmlFor="linkedin">
                  LinkedIn{" "}
                  <span className="text-stone-400 text-xs font-normal">
                    (optional, only if you'd like to be tagged when we share your review on LinkedIn)
                  </span>
                </label>
                <input
                  id="linkedin"
                  name="linkedin"
                  type="text"
                  value={form.linkedin}
                  onChange={handleChange}
                  className={inputClasses}
                  placeholder="linkedin.com/in/janedeveloper"
                />
              </div>

              {/* Prompts sit above the text area, full width */}
              <div>
                <aside className="bg-stone-100 border border-stone-300 rounded-lg p-4 mb-3">
                  <p className="text-sm font-semibold text-stone-700 mb-2">
                    Not sure what to write?
                  </p>
                  <ul className="list-disc list-outside pl-4 space-y-1.5 text-sm text-stone-500">
                    {PROMPTS.map((prompt) => (
                      <li key={prompt}>{prompt}</li>
                    ))}
                  </ul>
                </aside>

                <label className="block text-sm font-medium mb-1.5 text-stone-700" htmlFor="review">
                  Your review <span className="text-orange-500">*</span>
                </label>
                <textarea
                  id="review"
                  name="review"
                  required
                  value={form.review}
                  onChange={handleChange}
                  rows={11}
                  className={`${inputClasses} resize-y`}
                  placeholder="Share your experience in your own words..."
                />
                <p className="text-xs text-stone-400 mt-2">
                  Using AI to tidy up your wording is totally fine. Please just
                  avoid having it write the review from scratch, so I can
                  capture & share your authentic voice.
                </p>
              </div>

              {error && (
                <p className="text-red-500 text-sm font-medium" role="alert">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full md:w-auto px-8 py-3 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting..." : "Submit testimonial"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default ReviewForm;
